
import { Adresse } from '@/model/adresse';
import { Alerte } from '@/model/alerte';
import { ArticleLieuDepart } from '@/model/article-lieu-depart';
import { BrsTiers } from '@/model/brs-tiers';
import { cloneDeep, find, groupBy } from 'lodash';
import { endOfDay } from 'date-fns';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { length, gte, required, pick, or, eq, custom, revalidate, withMessage, optional } from 'form-validation-manager';
import { RouteName } from '@/router/routes';
import { DestinatairesAlerte } from '@/model/alerte-notifications';
import { InfosContact } from '@/model/infos-contact';

interface TreeviewItem<T> {
  id: number;
  name: string;
  value?: T;
  children?: TreeviewItem<T>[];
}

export default Vue.extend({
  name: 'EditAlerte',
  props: {
    idAlerte: {
      type: [Number, String],
      required: true
    },
    idArticle: {
      type: Number,
      required: false
    }
  },
  data: () => ({
    loading: true,
    alerte: null as Alerte,
    displayCardsError: false,
    disableNotificationsCard: false,
    form: {
      aldIds: [] as number[],
      criteres: {
        checkboxes: {}, // vide, init dans le created
        stockDispo: null as boolean,
        prixMini: null as number,
        prixMaxi: null as number,
        echeanceMini: null as Date,
        echeanceMaxi: null as Date
      },
      notifications: [] as DestinatairesAlerte[]

    }
  }),
  validations: {
    form: {
      aldIds: withMessage(
        length(gte(1)),
        'Veuillez sélectionner au moins un article'
      ),
      criteres: {
        $self: withMessage(
          or(
            pick('stockDispo', eq(true)),
            pick('prixMini', required()),
            pick('prixMaxi', required())
            // pick('echeanceMini', isDate()),
            // pick('echeanceMaxi', isDate())
          ),
          'Veuillez renseigner au moins un critère'
        ),
        prixMini: revalidate('form.criteres.prixMaxi'),
        prixMaxi: optional(custom(function (value) { return value < this.form.criteres.prixMini ? 'Doit être supérieur au prix min' : false; }))
        // echeanceMini: revalidate('form.criteres.echeanceMaxi'),
        // echeanceMaxi: optional(custom(function (value) { return isBefore(value, this.form.criteres.echeanceMini) ? 'Doit être supérieure à la date de début' : false; }))
      },
      notifications: custom(function (value) {
        return value.some((notif:DestinatairesAlerte) => notif.envoiSms1 || notif.envoiSms2 || notif.envoiEmail) ? false : 'Au moins un mode de notification doit être sélectionné';
      })
    }
  },
  computed: {
    ...mapGetters({
      alertes: 'alertes/alertesUtilisateur',
      infosContact: 'user/infosContact',
      _articlesLieux: 'articlesLieuxDepart/liste',
      infosTiers: 'tiers/infosTiers',
      user: 'user/user'
    }),
    adresseAdherent (): Adresse {
      return (this.infosTiers as BrsTiers).adresses.filter(adr => adr.typeAdr === 'F')[0];
    },
    articlesLieux (): ArticleLieuDepart[] {
      return (this._articlesLieux as ArticleLieuDepart[]).slice()
        .sort((a, b) => a.article.id - b.article.id || a.lieuDepart.libelle.localeCompare(b.lieuDepart.libelle));
    },
    listItems (): TreeviewItem<ArticleLieuDepart>[] {
      const aldByArticles = groupBy(this.articlesLieux as ArticleLieuDepart[], 'article.id');

      // const liste :TreeviewItem<ArticleLieuDepart>[]= [];
      let fakeId = -1;
      const generatefakeId = () => fakeId--;

      return Object.values(aldByArticles).map(aldList => {
        const article = aldList[0].article;
        const item: TreeviewItem<ArticleLieuDepart> = {
          id: null,
          name: article.libelle + (article.commentaire !== '' ? ` (${article.commentaire})` : '')
        };
        if (aldList.length === 1) {
          item.value = aldList[0];
          item.id = aldList[0].id;
        } else {
          item.id = generatefakeId();
          item.children = aldList.map(ald => {
            return {
              id: ald.id,
              name: ald.lieuDepart.libelle,
              value: ald
            };
          });
        }

        return item;
      });
    }
  },
  created () {
    Promise.all([
      this.loadInfosContact(),
      this.loadAlerte(),
      (this._articlesLieux && this._articlesLieux.length) ? Promise.resolve() : this.loadArticlesLieux()
    ]).then(() => {
      this.alerte = this.idAlerte === 'new' ? new Alerte() : find(this.alertes, { id: this.idAlerte });
      this.initForm(this.alerte);
      this.initCheckBoxes();
    }).finally(() => {
      if (this.idArticle) {
        this.form.aldIds = this.articlesLieux.filter(ald => ald.article.id === this.idArticle).map(ald => ald.id);
      }
      this.loading = false;
    });
  },
  methods: {
    ...mapActions({
      loadArticlesLieux: 'articlesLieuxDepart/loadListe',
      loadAlertes: 'alertes/loadAlertesUtilisateur',
      loadInfosContact: 'user/loadInfosContact',
      loadListeRegionUser: 'user/loadListeRegionUser',
      creerAlerte: 'alertes/creerAlerte',
      modifierAlerte: 'alertes/modifierAlerte'
    }),
    capitalizeFirstLetter (str: string) {
      return str.toLowerCase().replace(/(\W|^)\w/g, c => c.toUpperCase());
    },
    initCheckBoxes () {
      Object.keys(this.form.criteres).filter(c => c !== 'checkboxes').forEach(critere => {
        Object.defineProperty(this.form.criteres.checkboxes, critere, {
          get: () => {
            return (this.form.criteres as any)[critere] !== null;
          },
          set: (val) => {
            if (val) {
              (this.form.criteres as any)[critere] = undefined;
              (this.$refs[critere] as any).focus();
            } else {
              (this.form.criteres as any)[critere] = null;
            }
          }
        });
      });
    },
    loadAlerte () {
      if (this.idAlerte === 'new') {
        this.alerte = new Alerte();
        return Promise.resolve();
      } else {
        return ((this.alertes && this.alertes.length) ? Promise.resolve() : this.loadAlertes(this.user.username)).then(() => {
          const alerte = find(this.alertes, { id: this.idAlerte });

          if (!alerte) {
            this.$dialog.error({ title: 'Erreur', text: 'Alerte introuvable' });
            throw Error('Alerte introuvable');
          } else {
            this.alerte = cloneDeep(alerte);
          }
        });
      }
    },
    initForm (alerte: Alerte) {
      this.form.aldIds = alerte.articleLieuDepart.map(ald => ald.id);

      this.form.criteres.stockDispo = alerte.stockDispo || null;
      this.form.criteres.prixMini = alerte.prixMini || null;
      this.form.criteres.prixMaxi = alerte.prixMaxi || null;
      this.form.criteres.echeanceMini = alerte.echeanceMini || null;
      this.form.criteres.echeanceMaxi = alerte.echeanceMaxi || null;

      this.form.notifications = (this.infosContact as InfosContact[]).map(ic => {
        // eslint-disable-next-line eqeqeq
        const notif = find(alerte.destinatairesAlerte, n => n.idPersonne == ic.idPersonne);
        return {
          id: notif ? notif.id : null,
          envoiSms1: notif ? notif.envoiSms1 : false,
          envoiSms2: notif ? notif.envoiSms2 : false,
          envoiEmail: notif ? notif.envoiEmail : false,
          idPersonne: ic.idPersonne
        };
      });
    },
    getContactById (id:number) {
      // eslint-disable-next-line eqeqeq
      return find(this.infosContact as InfosContact[], ic => ic.idPersonne == id);
    },
    isTelMobile (num: string) {
      return /^0[67]/.test(num);
    },
    cancel () {
      this.$dialog.confirm({
        title: 'Confirmation',
        text: 'Toute modification sera perdue. Voulez-vous continuer ?',
        actions: {
          false: {
            text: 'Annuler',
            flat: false,
            outlined: true,
            color: 'grey'
          },
          true: {
            text: 'Continuer',
            color: 'primary',
            flat: false
          }
        }
      }).then(confirm => {
        if (confirm) {
          this.$router.push({ name: RouteName.MAIN_ALERTES_LISTE });
        }
      });
    },
    save () {
      this.displayCardsError = true;
      (this.$refs.form as any).validate();
      if (this.$fvm.$valid) {
        this.loading = true;

        this.alerte.articleLieuDepart = this.form.aldIds.map(id => find(this._articlesLieux, { id })).filter(ald => !!ald);

        this.alerte.stockDispo = this.form.criteres.stockDispo;
        this.alerte.prixMini = this.form.criteres.prixMini;
        this.alerte.prixMaxi = this.form.criteres.prixMaxi;
        this.alerte.echeanceMini = endOfDay(this.form.criteres.echeanceMini);
        this.alerte.echeanceMaxi = endOfDay(this.form.criteres.echeanceMaxi);

        this.alerte.destinatairesAlerte = this.form.notifications;

        this.alerte.actif = true;

        (this.alerte.id
          ? this.modifierAlerte({ utilisateur: this.user.username, alerte: this.alerte })
          : this.creerAlerte({ utilisateur: this.user.username, alerte: this.alerte }))
          .then(() => {
            this.$dialog.notify.success('Alerte enregistrée');
            this.$router.push({ name: RouteName.MAIN_ALERTES_LISTE });
          }).finally(() => {
            this.loading = false;
          });
      } else {
        if (this.$fvm.form.aldIds.$error) {
          document.querySelector('.card-sources').scrollIntoView();
        } else if (this.$fvm.form.criteres.$error) {
          document.querySelector('.card-criteres').scrollIntoView();
        } else if (this.$fvm.form.notifications.$error) {
          document.querySelector('.card-notifications').scrollIntoView();
        }
      }
    }
  }
});
